import { ReactElement, use, useEffect, useState } from 'react';
import { Controller, FieldValues, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BedRounded, LinkOutlined, NotificationImportantOutlined, SecurityRounded, VisibilityOffOutlined } from '@mui/icons-material';
import BroadcastOnPersonalOutlinedIcon from '@mui/icons-material/BroadcastOnPersonalOutlined';
import { Button, FormControl, InputLabel, MenuItem, Select, Switch, TextField, Typography } from '@mui/material';
import BreadCrumb from 'shared/components/bread-crumb/bread-crumb/bread-crumb';
import OrganisationBreadCrumb from 'shared/components/bread-crumb/organisation-bread-crumb/organisation-bread-crumb';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import Divider from 'shared/components/divider/divider';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import OfficeBuildingMarker from 'shared/components/icons/office-building-marker';
import SubscriptionIcon from 'shared/components/icons/subcription-icon';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import PopUp from 'shared/components/pop-up/pop-up';
import SettingsRow from 'shared/components/table-rows/settings-row/settings-row';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';
import OrganisationTreeNode from 'shared/features/organisation/models/organisation-tree-node';
import { useOrganisationUnitTreeContext } from 'shared/features/organisation/providers/organisation-unit-tree-provider';
import { DevicesProvider } from 'shared/providers/devices/providers/devices-provider';

import { useOrganisationUnitsProvider } from 'features/organisation/context/organisation-units-provider';
import { OrganisationFormName } from 'features/organisation/domain/enums/organisation-form-name';
import AlternativeDiversionOrganisationUnit from 'features/organisation/domain/models/alternative-diversion-organisation-unit';
import CreateOrganisationUnitCommand from 'features/organisation/domain/models/create-organisation-unit-command';
import EditOrganisationUnitCommand from 'features/organisation/domain/models/edit-organisation-unit-command';
import EmergencyAlarmRouteOrganisationUnit from 'features/organisation/domain/models/emergency-alarm-route-organisation-unit';
import OrganisationUnitDevice from 'features/organisation/domain/models/organisation-unit-device';
import {
  useCreateOrganisationUnit,
  useDeleteOrganisationUnit,
  useUpdateOrganisationUnit,
} from 'features/organisation/domain/queries/organisation-unit-queries';
import AlternativeDiversionValidator from 'features/organisation/domain/validators/alternative-divertion-validator';
import EmergencyAlarmRouteValidator from 'features/organisation/domain/validators/emergency-alarm-route-validator';
import NameValidator from 'features/organisation/domain/validators/name-validator';
import ResidentialLocationValidator from 'features/organisation/domain/validators/residential-location-validator';
import SubscriptionChannelValidator from 'features/organisation/domain/validators/subscription-channel-validator';
import Scenario from 'features/organisation/models/scenario';
import ElectronicClientRecord from 'features/organisation/views/organisation-details/components/electronic-client-record/electronic-client-record';
import ElectronicClientRecordProvider from 'features/organisation/views/organisation-details/components/electronic-client-record/providers/electronic-client-record-provider';
import OrganisationDetailsEmergencyAlarmRouteModal from 'features/organisation/views/organisation-details/components/organisation-details-emergency-alarm-route-modal/organisation-details-emergency-alarm-route-modal';
import OrganisationUnitDetailsHeader from 'features/organisation/views/organisation-details/components/organisation-details-header/organisation-details-header';
import OrganisationDetailsLinkableScenariosModal from 'features/organisation/views/organisation-details/components/organisation-details-linkable-scenarios-modal/organisation-details-linkable-scenarios-modal';
import OrganisationDetailsScenariosList from 'features/organisation/views/organisation-details/components/organisation-details-scenarios-list/organisation-details-scenarios-list';
import useResidentSettingsNavigation from 'features/organisation/views/organisation-details/hooks/resident-settings-navigation';
import OrganisationDetailsAlternativeDiversionModal from './components/organisation-details-alternative-diversion-modal/organisation-details-alternative-diversion-modal';
import OrganisationDetailsDevicesTable from './components/organisation-details-device-table/organisation-details-devices-table';
import OrganisationDetailsLinkableDevicesModal from './components/organisation-details-linkable-devices-modal/organisation-details-linkable-devices-modal.component';

import './organisation-details.component.scss';

import { AxiosError } from 'axios';
import MultiSelectField from 'shared/components/formFields/multi-select-field';
import SettingsMultiLineRow from 'shared/components/table-rows/settings-multi-line-row/settings-multi-line-row';
import { DeviceType, isG2Device } from 'shared/models/device-type';

import { isPairedWirelessIpUnitDevice } from 'features/device/domain/models/device';
import LinkableDevice from 'features/device/linkable-device/domain/models/linkable-device';
import { DeviceLinkProvider } from 'features/device/providers/device-link-provider/device-link-provider';

interface IProps {
  viewingMode: ViewingMode;
  upstreamParentBranch: OrganisationTreeNode[];
  siblings: OrganisationTreeNode[];
  selectedNode?: OrganisationTreeNode;
  parentNode?: OrganisationTreeNode;
  cancelAction: () => void;
  successAction: () => void;
  onViewingModeChanged: (viewingMode: ViewingMode) => void;
}

const OrganisationDetails = (props: Readonly<IProps>): ReactElement => {
  const { t } = useTranslation('organisation');
  const { t: deviceTypesTranslations } = useTranslation('deviceTypes');

  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const { enablePopUpGuard } = use(ConfirmationPopupProviderContext);
  const { hasPermission } = usePermissionAuthorization();
  const { navigateToResidentSettings } = useResidentSettingsNavigation();

  const [shouldShowDeleteConfirmationPopup, setShouldShowDeleteConfirmationPopup] = useState<boolean>(false);
  const [shouldShowIsLocationConfirmationPopup, setShouldShowIsLocationConfirmationPopup] = useState<boolean>(false);
  const [shouldShowErrorDeletePopup, setShouldShowErrorDeletePopup] = useState<boolean>(false);
  const [errorDeletePopupContent, setErrorDeletePopupContent] = useState<string>('');
  const [shouldShowErrorEditSavePopup, setShouldShowErrorEditSavePopup] = useState<boolean>(false);
  const [errorEditSavePopupContent, setErrorEditSavePopupContent] = useState<string>('');
  const [alternativeDiversionModalIsOpen, setAlternativeDiversionModalIsOpen] = useState<boolean>(false);
  const [emergencyAlarmRoutesModalIsOpen, setEmergencyAlarmRoutesModalIsOpen] = useState<boolean>(false);
  const [linkableDevicesModalIsOpen, setLinkableDevicesModalIsOpen] = useState<boolean>(false);
  const [linkableScenariosModalIsOpen, setLinkableScenariosModalIsOpen] = useState<boolean>(false);
  const [unlinkedDevices, setUnlinkedDevices] = useState<OrganisationUnitDevice[]>([]);
  const [unlinkedScenarios, setUnlinkedScenarios] = useState<Scenario[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [unlinkConfirmationWarningCurrentDevice, setUnlinkConfirmationWarningCurrentDevice] = useState<OrganisationUnitDevice | undefined>(
    undefined,
  );
  const [showIpUnitUnreachableError, setShowIpUnitUnreachableError] = useState<boolean>(false);

  const {
    mutateAsync: createOrganisationUnit,
    isSuccess: createOrganisationIsSuccess,
    error: createOrganisationError,
  } = useCreateOrganisationUnit();
  const {
    mutateAsync: deleteOrganisationUnit,
    isSuccess: deleteOrganisationIsSuccess,
    isError: deleteOrganisationIsError,
    error: deleteOrganisationError,
  } = useDeleteOrganisationUnit();
  const {
    mutateAsync: updateOrganisationUnit,
    isSuccess: updateOrganisationIsSuccess,
    error: updateOrganisationError,
  } = useUpdateOrganisationUnit();

  const { organisationUnitDetailsHook } = useOrganisationUnitsProvider();
  const { organisationUnitTree, refetch } = useOrganisationUnitTreeContext();

  const diversionTimeoutOptions = [
    <MenuItem data-testid={`diversion-timeout-item-fifteen-minutes`} key={`diversion-timeout-item-fifteen-minutes`} value={900}>
      {t('diversionTimeoutOptions.fifteenMinutes')}
    </MenuItem>,
    <MenuItem data-testid={`diversion-timeout-item-thirty-minutes`} key={`diversion-timeout-item-thirty-minutes`} value={1800}>
      {t('diversionTimeoutOptions.thirtyMinutes')}
    </MenuItem>,
    <MenuItem data-testid={`diversion-timeout-item-fourty-five-minutes`} key={`diversion-timeout-item-fourty-five-minutes`} value={2700}>
      {t('diversionTimeoutOptions.fourtyFiveMinutes')}
    </MenuItem>,
  ];

  const transmitterResidentSettingOptions: DeviceType[] = [DeviceType.G2Transmitter, DeviceType.DesoResidentTransmitter];

  const {
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    setValue,
    formState: { isDirty },
    trigger,
    clearErrors,
    reset,
    watch,
    setError,
  } = organisationUnitDetailsHook.form;

  const watchIsLocation = watch(OrganisationFormName.IsLocation);

  useEffect(() => {
    if (watchIsLocation) {
      setValue(OrganisationFormName.IsSubscriptionChannel, true);
      setValue(OrganisationFormName.ResidentialLocation, false);
      setValue(OrganisationFormName.IsHidden, false);
      setValue(OrganisationFormName.Scenarios, []);
    } else if (props.viewingMode !== 'viewing') {
      setValue(OrganisationFormName.IsSubscriptionChannel, false);
      setValue(OrganisationFormName.TransmitterResidentSettings, []);
    }
    clearErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchIsLocation]);

  useEffect(() => {
    enablePopUpGuard(isDirty);
  }, [isDirty]);

  useEffect(() => {
    clearErrors();
    if (props.selectedNode) {
      organisationUnitDetailsHook.loadOrganisationUnit(props.selectedNode.id);
    } else {
      reset(organisationUnitDetailsHook.defaultFormValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedNode]);

  useEffect(() => {
    clearErrors();
    setUnlinkedDevices([]);
    setUnlinkedScenarios([]);

    if (props.viewingMode === 'creation') {
      reset(organisationUnitDetailsHook.defaultFormValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.viewingMode]);

  useEffect(() => {
    if (
      !organisationUnitDetailsHook.isLoadingSelectedOrganisationUnit &&
      organisationUnitDetailsHook.selectedOrganisationUnit &&
      props.viewingMode === 'viewing'
    ) {
      const electronicClientRecordLocation = !organisationUnitDetailsHook.selectedOrganisationUnit.electronicClientRecordLocation
        ? {}
        : { ...organisationUnitDetailsHook.selectedOrganisationUnit.electronicClientRecordLocation, isUnlinked: false };

      reset({
        [OrganisationFormName.Name]: organisationUnitDetailsHook.selectedOrganisationUnit.name,
        [OrganisationFormName.IsLocation]: organisationUnitDetailsHook.selectedOrganisationUnit.isLocation,
        [OrganisationFormName.ResidentialLocation]: organisationUnitDetailsHook.selectedOrganisationUnit.isResidentialLocation,
        [OrganisationFormName.IsSubscriptionChannel]: organisationUnitDetailsHook.selectedOrganisationUnit.isSubscriptionChannel,
        [OrganisationFormName.IsHidden]: organisationUnitDetailsHook.selectedOrganisationUnit.isHidden,
        [OrganisationFormName.Devices]: organisationUnitDetailsHook.selectedOrganisationUnit.devices,
        [OrganisationFormName.Scenarios]: organisationUnitDetailsHook.selectedOrganisationUnit.scenarios,
        [OrganisationFormName.DiversionTimeout]: organisationUnitDetailsHook.selectedOrganisationUnit.diversionTimeout,
        [OrganisationFormName.DiversionTimeoutEnabled]: !!organisationUnitDetailsHook.selectedOrganisationUnit.diversionTimeout,
        [OrganisationFormName.IntentionOutOfBedEnabled]: organisationUnitDetailsHook.selectedOrganisationUnit.intentionOutOfBedEnabled,
        [OrganisationFormName.AlternativeDiversionEnabled]:
          organisationUnitDetailsHook.selectedOrganisationUnit.alternativeDiversionOrganisationUnits.length > 0,
        [OrganisationFormName.AlternativeDiversionOrganisationUnits]:
          organisationUnitDetailsHook.selectedOrganisationUnit.alternativeDiversionOrganisationUnits,
        [OrganisationFormName.EmergencyAlarmRoutesEnabled]:
          organisationUnitDetailsHook.selectedOrganisationUnit.alternativeEmergencyAlarmRouteOrganisationUnits.length > 0,
        [OrganisationFormName.EmergencyAlarmRouteOrganisationUnits]:
          organisationUnitDetailsHook.selectedOrganisationUnit.alternativeEmergencyAlarmRouteOrganisationUnits,
        [OrganisationFormName.ElectronicClientRecordRoom]: electronicClientRecordLocation,
        [OrganisationFormName.TransmitterResidentSettings]:
          organisationUnitDetailsHook.selectedOrganisationUnit.transmitterResidentSettings ?? [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationUnitDetailsHook.isLoadingSelectedOrganisationUnit]);

  const findOrganisationTreeNodeById = (tree: OrganisationTreeNode, id: string): OrganisationTreeNode | undefined => {
    if (!tree) {
      return undefined;
    }

    if (tree.id === id) {
      return tree;
    }

    for (const child of tree.childOrganisationUnits) {
      const foundNode = findOrganisationTreeNodeById(child, id);
      if (foundNode) {
        return foundNode;
      }
    }

    return undefined;
  };

  useEffect(() => {
    if (
      !organisationUnitTree ||
      organisationUnitDetailsHook.isLoadingSelectedOrganisationUnit ||
      !organisationUnitDetailsHook.selectedOrganisationUnit
    ) {
      return;
    }
  }, [organisationUnitDetailsHook.isLoadingSelectedOrganisationUnit]);

  const submitForm = async (fieldValues: FieldValues): Promise<void> => {
    setIsSubmitting(true);
    const validateAlternativeDiversionSuccess = validateAlternativeDiversionOnSubmit();
    const validateEmergencyAlarmRoutesSuccess = validateEmergencyAlarmRoutesOnSubmit();

    if (
      !getValues(OrganisationFormName.ResidentialLocation) &&
      Object.keys(getValues(OrganisationFormName.ElectronicClientRecordRoom)).length > 0
    ) {
      setErrorEditSavePopupContent(t('deleteErrors.hasLinkedElectronicClientRecord'));
      setShouldShowErrorEditSavePopup(true);
      setIsSubmitting(false);
      return;
    }

    if (
      props.viewingMode === 'editing' &&
      organisationUnitDetailsHook.selectedOrganisationUnit?.hasActiveCallPlan &&
      ((organisationUnitDetailsHook.selectedOrganisationUnit.isSubscriptionChannel &&
        !getValues(OrganisationFormName.IsSubscriptionChannel)) ||
        (!organisationUnitDetailsHook.selectedOrganisationUnit.isResidentialLocation &&
          getValues(OrganisationFormName.ResidentialLocation)))
    ) {
      setErrorEditSavePopupContent(t('deleteErrors.hasActiveCallPlan'));
      setShouldShowErrorEditSavePopup(true);
      setIsSubmitting(false);
      return;
    }

    if (!validateAlternativeDiversionSuccess || !validateEmergencyAlarmRoutesSuccess) {
      setIsSubmitting(false);
      return;
    }
    if (!props.selectedNode?.isLocation && fieldValues.isLocation) {
      setShouldShowIsLocationConfirmationPopup(true);
    } else {
      await handleSubmitForm();
    }
  };

  const handleSubmitForm = async (): Promise<void> => {
    try {
      switch (props.viewingMode) {
        case 'creation':
          await submitCreation();
          break;
        case 'editing':
          await submitEdit();
          break;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    let result: boolean = false;
    let error;
    switch (props.viewingMode) {
      case 'creation':
        result = createOrganisationIsSuccess;
        error = createOrganisationError;
        break;
      case 'editing':
        result = updateOrganisationIsSuccess;
        error = updateOrganisationError;
        break;
    }

    if (!result && error instanceof AxiosError && error.status === 424) {
      setShowIpUnitUnreachableError(true);
    } else if (!result && error) {
      setErrorMessage(error ?? undefined);
    } else if (result && !error) {
      props.successAction();
      organisationUnitDetailsHook.form.reset();
      organisationUnitDetailsHook.refresh();
      if (refetch && typeof refetch === 'function') refetch();
    }
  }, [createOrganisationIsSuccess, createOrganisationError, updateOrganisationIsSuccess, updateOrganisationError]);

  const submitCreation = async (): Promise<void> => {
    const electronicClientRecordRoom = getValues(OrganisationFormName.ElectronicClientRecordRoom);

    const createOrganisationUnitCommand = {
      [OrganisationFormName.Name]: getValues(OrganisationFormName.Name).trim(),
      [OrganisationFormName.IsLocation]: getValues(OrganisationFormName.IsLocation),
      isResidentialLocation: getValues(OrganisationFormName.ResidentialLocation),
      [OrganisationFormName.IsSubscriptionChannel]: getValues(OrganisationFormName.IsSubscriptionChannel),
      [OrganisationFormName.IsHidden]: getValues(OrganisationFormName.IsHidden),
      parentId: props.parentNode?.id,
      deviceIds: getValues(OrganisationFormName.Devices).map((device: OrganisationUnitDevice) => device.id),
      scenarioIds: getValues(OrganisationFormName.Scenarios).map((scenario: Scenario) => scenario.id),
      [OrganisationFormName.IntentionOutOfBedEnabled]: getValues(OrganisationFormName.IntentionOutOfBedEnabled),
      alternativeDiversionOrganisationUnitIds: getValues(OrganisationFormName.AlternativeDiversionEnabled)
        ? getValues(OrganisationFormName.AlternativeDiversionOrganisationUnits).map((organisationUnit) => organisationUnit.id)
        : [],
      alternativeEmergencyAlarmRouteOrganisationUnitIds: getValues(OrganisationFormName.EmergencyAlarmRoutesEnabled)
        ? getValues(OrganisationFormName.EmergencyAlarmRouteOrganisationUnits).map((organisationUnit) => organisationUnit.id)
        : [],
      electronicClientRecordLocation:
        electronicClientRecordRoom?.isUnlinked || Object.keys(electronicClientRecordRoom).length === 0
          ? null
          : getValues(OrganisationFormName.ElectronicClientRecordRoom),
      transmitterResidentSettings: getValues(OrganisationFormName.TransmitterResidentSettings),
    } as CreateOrganisationUnitCommand;

    await createOrganisationUnit(createOrganisationUnitCommand);
  };

  const submitEdit = async (): Promise<void> => {
    const electronicClientRecordRoom = getValues(OrganisationFormName.ElectronicClientRecordRoom);

    const editOrganisationUnitCommand = {
      id: props.selectedNode?.id,
      name: getValues(OrganisationFormName.Name).trim(),
      isLocation: getValues(OrganisationFormName.IsLocation),
      isResidentialLocation: getValues(OrganisationFormName.ResidentialLocation),
      isSubscriptionChannel: getValues(OrganisationFormName.IsSubscriptionChannel),
      isHidden: getValues(OrganisationFormName.IsHidden),
      diversionTimeout: getValues(OrganisationFormName.DiversionTimeoutEnabled) ? getValues(OrganisationFormName.DiversionTimeout) : null,
      parentId: props.parentNode?.id,
      depth: props.selectedNode?.depth,
      deviceIds: getValues(OrganisationFormName.Devices).map((device: OrganisationUnitDevice) => device.id),
      scenarioIds: getValues(OrganisationFormName.Scenarios).map((scenario: Scenario) => scenario.id),
      intentionOutOfBedEnabled: getValues(OrganisationFormName.IntentionOutOfBedEnabled),
      alternativeDiversionOrganisationUnitIds: getValues(OrganisationFormName.AlternativeDiversionEnabled)
        ? getValues(OrganisationFormName.AlternativeDiversionOrganisationUnits).map((organisationUnit) => organisationUnit.id)
        : [],
      alternativeEmergencyAlarmRouteOrganisationUnitIds: getValues(OrganisationFormName.EmergencyAlarmRoutesEnabled)
        ? getValues(OrganisationFormName.EmergencyAlarmRouteOrganisationUnits).map((organisationUnit) => organisationUnit.id)
        : [],
      electronicClientRecordLocation:
        electronicClientRecordRoom?.isUnlinked || Object.keys(electronicClientRecordRoom).length === 0
          ? null
          : getValues(OrganisationFormName.ElectronicClientRecordRoom),
      transmitterResidentSettings: getValues(OrganisationFormName.TransmitterResidentSettings),
    } as EditOrganisationUnitCommand;

    await updateOrganisationUnit(editOrganisationUnitCommand);
  };

  const handleCancel = (): void => {
    if (props.viewingMode === 'editing' && organisationUnitDetailsHook.selectedOrganisationUnit) {
      reset({
        name: organisationUnitDetailsHook.selectedOrganisationUnit.name,
        isLocation: organisationUnitDetailsHook.selectedOrganisationUnit.isLocation,
        residentialLocation: organisationUnitDetailsHook.selectedOrganisationUnit.isResidentialLocation,
        isSubscriptionChannel: organisationUnitDetailsHook.selectedOrganisationUnit.isSubscriptionChannel,
        isHidden: organisationUnitDetailsHook.selectedOrganisationUnit.isHidden,
        devices: organisationUnitDetailsHook.selectedOrganisationUnit.devices,
        scenarios: organisationUnitDetailsHook.selectedOrganisationUnit.scenarios,
        diversionTimeout: organisationUnitDetailsHook.selectedOrganisationUnit.diversionTimeout,
        diversionTimeoutEnabled: !!organisationUnitDetailsHook.selectedOrganisationUnit.diversionTimeout,
        intentionOutOfBedEnabled: organisationUnitDetailsHook.selectedOrganisationUnit.intentionOutOfBedEnabled,
        alternativeDiversionEnabled: organisationUnitDetailsHook.selectedOrganisationUnit.alternativeDiversionOrganisationUnits.length > 0,
        alternativeDiversionOrganisationUnits: organisationUnitDetailsHook.selectedOrganisationUnit.alternativeDiversionOrganisationUnits,
        emergencyAlarmRoutesEnabled:
          organisationUnitDetailsHook.selectedOrganisationUnit.alternativeEmergencyAlarmRouteOrganisationUnits.length > 0,
        emergencyAlarmRouteOrganisationUnits:
          organisationUnitDetailsHook.selectedOrganisationUnit.alternativeEmergencyAlarmRouteOrganisationUnits,
        electronicClientRecordRoom: organisationUnitDetailsHook.selectedOrganisationUnit.electronicClientRecordLocation ?? {},
        transmitterResidentSettings: organisationUnitDetailsHook.selectedOrganisationUnit.transmitterResidentSettings ?? [],
      });
    } else {
      reset(organisationUnitDetailsHook.defaultFormValues);
    }
    props.cancelAction();
  };

  const handleOnRemoveButtonClicked = (): void => {
    const errorDeleteMessages = [];

    if (props.selectedNode?.childOrganisationUnits && props.selectedNode.childOrganisationUnits.length > 0) {
      errorDeleteMessages.push(t('deleteErrors.isParent'));
    }

    if (
      organisationUnitDetailsHook.selectedOrganisationUnit?.devices &&
      organisationUnitDetailsHook.selectedOrganisationUnit.devices.length > 0
    ) {
      errorDeleteMessages.push(t('deleteErrors.hasLinkedDevices'));
    }

    if (
      organisationUnitDetailsHook.selectedOrganisationUnit?.gateways &&
      organisationUnitDetailsHook.selectedOrganisationUnit.gateways.length > 0
    ) {
      errorDeleteMessages.push(t('deleteErrors.hasLinkedGateways'));
    }

    if (
      organisationUnitDetailsHook.selectedOrganisationUnit?.scenarios &&
      organisationUnitDetailsHook.selectedOrganisationUnit.scenarios.length > 0
    ) {
      errorDeleteMessages.push(t('deleteErrors.hasLinkedScenarios'));
    }

    if (
      organisationUnitDetailsHook.selectedOrganisationUnit?.systems &&
      organisationUnitDetailsHook.selectedOrganisationUnit.systems.length > 0
    ) {
      errorDeleteMessages.push(t('deleteErrors.hasLinkedSystems'));
    }

    if (
      organisationUnitDetailsHook.selectedOrganisationUnit?.roles &&
      organisationUnitDetailsHook.selectedOrganisationUnit.roles.length > 0
    ) {
      errorDeleteMessages.push(t('deleteErrors.hasLinkedRole'));
    }

    if (
      organisationUnitDetailsHook.selectedOrganisationUnit?.isAlternativeDiversionOrganisationUnit ||
      organisationUnitDetailsHook.selectedOrganisationUnit?.isAlternativeEmergencyAlarmRouteOrganisationUnit
    ) {
      errorDeleteMessages.push(t('deleteErrors.hasLinkedAlternativeRules'));
    }

    if (
      (organisationUnitDetailsHook.selectedOrganisationUnit?.alternativeEmergencyAlarmRouteOrganisationUnits &&
        organisationUnitDetailsHook.selectedOrganisationUnit.alternativeEmergencyAlarmRouteOrganisationUnits.length > 0) ||
      (organisationUnitDetailsHook.selectedOrganisationUnit?.alternativeDiversionOrganisationUnits &&
        organisationUnitDetailsHook.selectedOrganisationUnit.alternativeDiversionOrganisationUnits.length > 0)
    ) {
      errorDeleteMessages.push(t('deleteErrors.hasLinkedAlternativeRoutingRules'));
    }

    if (organisationUnitDetailsHook.selectedOrganisationUnit?.electronicClientRecordLocation) {
      errorDeleteMessages.push(t('deleteErrors.hasLinkedElectronicClientRecord'));
    }

    if (organisationUnitDetailsHook.selectedOrganisationUnit?.hasActiveCallPlan) {
      errorDeleteMessages.push(t('deleteErrors.hasActiveCallPlan'));
    }

    if (errorDeleteMessages.length > 0) {
      setErrorDeletePopupContent(errorDeleteMessages.join('\r\n \r\n'));
      setShouldShowErrorDeletePopup(true);
    } else {
      setShouldShowDeleteConfirmationPopup(true);
    }
  };

  const handleOnCancelButtonClicked = (): void => {
    if (organisationUnitDetailsHook.selectedOrganisationUnit) {
      setValue(OrganisationFormName.Devices, organisationUnitDetailsHook.selectedOrganisationUnit.devices);
      setValue(OrganisationFormName.Scenarios, organisationUnitDetailsHook.selectedOrganisationUnit.scenarios);
    }
    handleCancel();
  };

  const handleDeleteOrganisationUnit = async (): Promise<void> => {
    if (props.selectedNode?.id) {
      await deleteOrganisationUnit(props.selectedNode.id);
    }

    if (refetch && typeof refetch === 'function') refetch();
    setShouldShowDeleteConfirmationPopup(false);
  };

  useEffect(() => {
    if (deleteOrganisationIsSuccess) {
      props.onViewingModeChanged('none');
    } else if (deleteOrganisationIsError && deleteOrganisationError) {
      setErrorMessage(deleteOrganisationError);
    }
  }, [deleteOrganisationIsSuccess, deleteOrganisationIsError, deleteOrganisationError]);

  const handleOnViewingModeChanged = (viewingMode: ViewingMode): void => {
    props.onViewingModeChanged(viewingMode);
  };

  const handleOnAlternativeDiversionButtonClicked = (): void => {
    setAlternativeDiversionModalIsOpen(true);
  };

  const handleOnEmergencyAlarmRoutesButtonClicked = (): void => {
    setEmergencyAlarmRoutesModalIsOpen(true);
  };

  const handleOnDeviceConnectionButtonClicked = (): void => {
    setLinkableDevicesModalIsOpen(true);
  };

  const handleOnScenarioConnectionButtonClicked = (): void => {
    setLinkableScenariosModalIsOpen(true);
  };

  const handleOnAlternativeDiversionLinkButtonClicked = (selectedAlternativeDiversions: AlternativeDiversionOrganisationUnit[]): void => {
    setValue(OrganisationFormName.AlternativeDiversionOrganisationUnits, selectedAlternativeDiversions, {
      shouldDirty: true,
    });

    if (errors.alternativeDiversionEnabled?.type === 'onSubmitOnly') {
      clearErrors(OrganisationFormName.AlternativeDiversionEnabled);
    }
    setAlternativeDiversionModalIsOpen(false);
  };

  const handleOnEmergencyAlarmRouteLinkButtonClicked = (selectedEmergencyAlarmRoutes: EmergencyAlarmRouteOrganisationUnit[]): void => {
    setValue(OrganisationFormName.EmergencyAlarmRouteOrganisationUnits, selectedEmergencyAlarmRoutes, {
      shouldDirty: true,
    });

    if (errors.alternativeDiversionEnabled?.type === 'onSubmitOnly') {
      clearErrors(OrganisationFormName.AlternativeDiversionEnabled);
    }
    setEmergencyAlarmRoutesModalIsOpen(false);
  };

  const handleOnDevicesLinkButtonClicked = (selectedDevices: OrganisationUnitDevice[]): void => {
    setUnlinkedDevices((current) =>
      current.filter((unlinkedDevice) => organisationUnitDetailsHook.isSelectedDevice(unlinkedDevice, selectedDevices)),
    );

    setValue(OrganisationFormName.Devices, [...getValues(OrganisationFormName.Devices)].concat(selectedDevices), {
      shouldDirty: true,
    });

    setLinkableDevicesModalIsOpen(false);
  };

  const handleOnScenariosLinkButtonClicked = (selectedScenarios: Scenario[]): void => {
    setUnlinkedScenarios((current) =>
      current.filter((unlinkedScenario) => organisationUnitDetailsHook.isSelectedScenario(unlinkedScenario, selectedScenarios)),
    );

    setValue(OrganisationFormName.Scenarios, [...getValues(OrganisationFormName.Scenarios)].concat(selectedScenarios), {
      shouldDirty: true,
    });

    setLinkableScenariosModalIsOpen(false);
  };

  const handleOnUnlinkDeviceButtonClicked = (selectedDevice: OrganisationUnitDevice): void => {
    if (selectedDevice?.type && isG2Device(selectedDevice.type) && isPairedWirelessIpUnitDevice(selectedDevice.system?.type)) {
      setUnlinkConfirmationWarningCurrentDevice(selectedDevice);
      return;
    }

    unlinkDevice(selectedDevice);
  };

  const handleWarningConfirmed = (): void => {
    if (unlinkConfirmationWarningCurrentDevice) {
      unlinkDevice(unlinkConfirmationWarningCurrentDevice);
      setUnlinkConfirmationWarningCurrentDevice(undefined);
    }
  };

  const unlinkDevice = (selectedDevice: OrganisationUnitDevice): void => {
    setUnlinkedDevices([...unlinkedDevices, selectedDevice]);
    setValue(
      OrganisationFormName.Devices,
      [...getValues(OrganisationFormName.Devices)].filter((device) => device.id !== selectedDevice.id),
      { shouldDirty: true },
    );
  };

  const handleOnUnlinkScenarioButtonClicked = (selectedScenario: Scenario): void => {
    setUnlinkedScenarios([...unlinkedScenarios, selectedScenario]);
    setValue(
      OrganisationFormName.Scenarios,
      [...getValues(OrganisationFormName.Scenarios)].filter((scenario) => scenario.id !== selectedScenario.id),
      { shouldDirty: true },
    );
  };

  const validateName = (): boolean | string => {
    return NameValidator.isValid(getValues(OrganisationFormName.Name), props.viewingMode, props.parentNode, props.siblings)
      ? true
      : t('uniqueNameHelperText', {
          name: getValues(OrganisationFormName.Name),
          parentName: props.parentNode?.name,
        });
  };

  const validateIsSubscriptionChannel = (): boolean | string => {
    if (
      !getValues(OrganisationFormName.IsSubscriptionChannel) &&
      props.viewingMode !== 'creation' &&
      (organisationUnitDetailsHook.selectedOrganisationUnit?.isAlternativeDiversionOrganisationUnit ||
        organisationUnitDetailsHook.selectedOrganisationUnit?.isAlternativeEmergencyAlarmRouteOrganisationUnit)
    ) {
      return t('isSubscriptionAlternativerouteHelperText');
    }
    if (getValues(OrganisationFormName.IsSubscriptionChannel) && !getValues(OrganisationFormName.IsLocation)) {
      const checkChildrenResult = SubscriptionChannelValidator.hasLocationInChildren(props.selectedNode?.childOrganisationUnits);
      if (checkChildrenResult) {
        return t('isSubscriptionChannelHelperText');
      }

      const checkParentLocationResult = SubscriptionChannelValidator.hasLocationInParents(props.parentNode?.id, props.upstreamParentBranch);
      if (!checkParentLocationResult) {
        return t('noLocationInUpstreamParentBranchSubscriptionHelperText');
      }
    }
    return true;
  };

  const validateResidentialLocation = (): boolean | string => {
    if (getValues(OrganisationFormName.ResidentialLocation)) {
      const checkSingleResidenceResult = ResidentialLocationValidator.checkSingleResidence(
        props.viewingMode,
        props.parentNode,
        props.selectedNode,
        props.upstreamParentBranch,
      );
      if (!checkSingleResidenceResult) {
        return t('singleResidentialLocationHelperText');
      }

      const checkHasLocationInParents = ResidentialLocationValidator.hasLocationInParents(props.parentNode?.id, props.upstreamParentBranch);
      if (!checkHasLocationInParents) {
        return t('noLocationInUpstreamParentBranchResidentialHelperText');
      }
    }

    return true;
  };

  const validateAlternativeDiversion = (): boolean | string => {
    if (getValues(OrganisationFormName.AlternativeDiversionEnabled)) {
      const checkSingleResidenceResult = AlternativeDiversionValidator.checkSingleAlternativeDiversionRule(
        props.viewingMode,
        props.parentNode,
        props.selectedNode,
        props.upstreamParentBranch,
      );
      if (!checkSingleResidenceResult) {
        return t('singleAlternativeDiversionHelperText');
      }
    }
    return true;
  };

  const validateAlternativeDiversionOnSubmit = (): boolean => {
    const alternativeDiversionEnabled = getValues(OrganisationFormName.AlternativeDiversionEnabled);
    if (alternativeDiversionEnabled) {
      const anyAlternativeDiversionOrganisationUnitIds = getValues(OrganisationFormName.AlternativeDiversionOrganisationUnits).length > 0;
      if (!anyAlternativeDiversionOrganisationUnitIds) {
        setError(OrganisationFormName.AlternativeDiversionEnabled, {
          type: 'onSubmitOnly',
          message: t('atleastOneOrganisationUnitSelectedHelperText'),
        });
        return false;
      }
    }
    return true;
  };

  const validateEmergencyAlarmRoutes = (): boolean | string => {
    if (getValues(OrganisationFormName.EmergencyAlarmRoutesEnabled)) {
      const checkSingleResidenceResult = EmergencyAlarmRouteValidator.checkSingleEmergencyAlarmRouteRule(
        props.viewingMode,
        props.parentNode,
        props.selectedNode,
        props.upstreamParentBranch,
      );
      if (!checkSingleResidenceResult) {
        return t('singleAlternativeEmergencyAlarmRuleHelperText');
      }
    }
    return true;
  };

  const validateEmergencyAlarmRoutesOnSubmit = (): boolean => {
    const emergencyAlarmRouteEnabled = getValues(OrganisationFormName.EmergencyAlarmRoutesEnabled);
    if (emergencyAlarmRouteEnabled) {
      const anyEmergencyAlarmRouteOrganisationUnitIds = getValues(OrganisationFormName.EmergencyAlarmRouteOrganisationUnits).length > 0;
      if (!anyEmergencyAlarmRouteOrganisationUnitIds) {
        setError(OrganisationFormName.EmergencyAlarmRoutesEnabled, {
          type: 'onSubmitOnly',
          message: t('atleastOneOrganisationUnitSelectedHelperText'),
        });
        return false;
      }
    }
    return true;
  };

  const registerOptions = {
    name: {
      required: t('requiredHelperText'),
      validate: validateName,
    },
    isSubscriptionChannel: {
      validate: validateIsSubscriptionChannel,
      onChange: (): void => {
        trigger(OrganisationFormName.IsSubscriptionChannel);
        if (!getValues(OrganisationFormName.IsSubscriptionChannel)) {
          setValue(OrganisationFormName.AlternativeDiversionEnabled, false);
          setValue(OrganisationFormName.EmergencyAlarmRoutesEnabled, false);
        } else {
          clearErrors(OrganisationFormName.AlternativeDiversionEnabled);
        }
      },
    },
    residentialLocation: {
      validate: validateResidentialLocation,
      onChange: (): Promise<boolean> => trigger(OrganisationFormName.ResidentialLocation),
    },
    alternativeDiversionEnabled: {
      validate: validateAlternativeDiversion,
      onChange: (): void => {
        trigger(OrganisationFormName.AlternativeDiversionEnabled);
        if (errors.alternativeDiversionEnabled?.type === 'onSubmitOnly') {
          clearErrors(OrganisationFormName.AlternativeDiversionEnabled);
        }
      },
    },
    emergencyAlarmRouteEnabled: {
      validate: validateEmergencyAlarmRoutes,
      onChange: (): void => {
        trigger(OrganisationFormName.EmergencyAlarmRoutesEnabled);
        if (errors.emergencyAlarmRoutesEnabled?.type === 'onSubmitOnly') {
          clearErrors(OrganisationFormName.EmergencyAlarmRoutesEnabled);
        }
      },
    },
  };

  const getBreadCrumbs = (unit: AlternativeDiversionOrganisationUnit): string[] => {
    const parentLocation = organisationUnitDetailsHook.getParentLocation(props.selectedNode, props.upstreamParentBranch);
    if (!parentLocation) {
      return unit.breadCrumbs;
    }

    const parentIndex = unit.breadCrumbs.findIndex((breadcrumb) => breadcrumb === parentLocation.name);

    return parentIndex !== -1 ? unit.breadCrumbs.slice(parentIndex) : unit.breadCrumbs;
  };

  const diversionTimeoutEnabled = organisationUnitDetailsHook.form.watch(OrganisationFormName.DiversionTimeoutEnabled);
  useEffect(() => {
    if (diversionTimeoutEnabled) {
      setValue(OrganisationFormName.DiversionTimeout, organisationUnitDetailsHook.selectedOrganisationUnit?.diversionTimeout ?? 900, {
        shouldDirty: true,
      });
    }
  }, [diversionTimeoutEnabled]);

  return (
    <div className="organisation-details">
      <FormProvider {...organisationUnitDetailsHook.form}>
        <form onSubmit={handleSubmit(submitForm)}>
          <OrganisationUnitDetailsHeader
            headerText={props.viewingMode !== 'creation' ? props.selectedNode?.name : undefined}
            breadCrumbElement={
              props.selectedNode?.depth !== 1 || props.viewingMode === 'creation' ? (
                <div className="organisation-bread-crumb-container">
                  <OrganisationBreadCrumb rootNodeId={props.parentNode?.id} upstreamParentBranch={props.upstreamParentBranch} />
                </div>
              ) : (
                <></>
              )
            }
            viewingMode={props.viewingMode}
            selectedNodeIsRoot={props.selectedNode?.depth === 1}
            hasErrors={
              !!errors.name ||
              !!errors.isSubscriptionChannel ||
              !!errors.residentialLocation ||
              !!errors.alternativeDiversionEnabled ||
              !!errors.emergencyAlarmRoutesEnabled
            }
            isScrolling={organisationUnitDetailsHook.isScrolling}
            isSubmitting={isSubmitting}
            showResidentSettingsNavigation={
              props.viewingMode === 'viewing' &&
              props.selectedNode?.isResidentialLocation === true &&
              hasPermission(Permission.ReadResidentsSettings)
            }
            onCancelButtonClicked={handleOnCancelButtonClicked}
            onViewingModeChanged={handleOnViewingModeChanged}
            onRemoveButtonClicked={handleOnRemoveButtonClicked}
            onResidentSettingsNavigation={() => {
              if (props.selectedNode) {
                navigateToResidentSettings(props.selectedNode);
              }
            }}
          />

          <div className={`body ${props.viewingMode === 'viewing' && 'is-viewing'}`} onScroll={organisationUnitDetailsHook.handleScroll}>
            {props.viewingMode !== 'viewing' && (
              <Controller
                name={OrganisationFormName.Name}
                control={control}
                rules={registerOptions.name}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="nameInput"
                    label={t('nameFormLabel')}
                    variant="outlined"
                    error={!!errors.name}
                    helperText={errors.name?.message?.toString()}
                  />
                )}
              />
            )}

            <Divider className="organisation-units-details-divider-first-item" startText={t('settingsDividerText')} />

            {organisationUnitDetailsHook.isLoadingSelectedOrganisationUnit ? (
              <div className="organisation-settings-loader">
                <LoadingIndicator />
              </div>
            ) : (
              <div className="organisation-settings-container">
                {!organisationUnitDetailsHook.organisationUnitTreeBranchContainsLocation(
                  props.selectedNode,
                  props.parentNode,
                  props.upstreamParentBranch,
                ) && (
                  <Controller
                    name={OrganisationFormName.IsLocation}
                    control={control}
                    render={({ field }) => (
                      <SettingsRow
                        icon={<OfficeBuildingMarker />}
                        text={t('isLocationSwitchText')}
                        formField={
                          <Switch
                            {...field}
                            type="checkbox"
                            value={field.value}
                            checked={field.value}
                            // prettier-ignore
                            // @ts-ignore
                            inputProps={{ "data-testid": "isLocationSwitch" }}
                            disabled={
                              props.viewingMode === 'viewing' ||
                              (props.selectedNode?.childOrganisationUnits.length !== 0 && props.selectedNode?.isLocation)
                            }
                          />
                        }
                      />
                    )}
                  />
                )}
                <Controller
                  name={OrganisationFormName.IsSubscriptionChannel}
                  control={control}
                  rules={registerOptions.isSubscriptionChannel}
                  render={({ field }) => (
                    <SettingsRow
                      icon={<SubscriptionIcon />}
                      text={t('isSubscriptionChannelSwitchText')}
                      hasError={!!errors.isSubscriptionChannel}
                      helperText={errors.isSubscriptionChannel?.message?.toString()}
                      formField={
                        <Switch
                          {...field}
                          type="checkbox"
                          value={field.value}
                          checked={field.value}
                          // prettier-ignore
                          // @ts-ignore
                          inputProps={{ "data-testid": "isSubscriptionChannelSwitch" }}
                          disabled={props.viewingMode === 'viewing' || getValues(OrganisationFormName.IsLocation) === true}
                        />
                      }
                    />
                  )}
                />
                <Controller
                  name={OrganisationFormName.ResidentialLocation}
                  control={control}
                  rules={registerOptions.residentialLocation}
                  render={({ field }) => (
                    <SettingsRow
                      icon={<BedRounded />}
                      text={t('residentialLocationSwitchText')}
                      hasError={!!errors.residentialLocation}
                      helperText={errors.residentialLocation?.message?.toString()}
                      formField={
                        <Switch
                          {...field}
                          type="checkbox"
                          value={field.value}
                          checked={field.value}
                          // prettier-ignore
                          // @ts-ignore
                          inputProps={{ "data-testid": "residentialSwitch" }}
                          disabled={props.viewingMode === 'viewing' || getValues(OrganisationFormName.IsLocation) === true}
                        />
                      }
                    />
                  )}
                />
                <Controller
                  name={OrganisationFormName.IsHidden}
                  control={control}
                  render={({ field }) => (
                    <SettingsRow
                      icon={<VisibilityOffOutlined />}
                      text={t('isHiddenSwitchText')}
                      formField={
                        <Switch
                          {...field}
                          type="checkbox"
                          value={field.value}
                          checked={field.value}
                          // prettier-ignore
                          // @ts-ignore
                          inputProps={{ "data-testid": "isHiddenSwitch" }}
                          disabled={props.viewingMode === 'viewing' || getValues(OrganisationFormName.IsLocation) === true}
                        />
                      }
                    />
                  )}
                />
                {watchIsLocation && (
                  <Controller
                    name={OrganisationFormName.TransmitterResidentSettings}
                    control={control}
                    render={({ field }) => (
                      <SettingsMultiLineRow
                        icon={<BroadcastOnPersonalOutlinedIcon />}
                        title={t('transmitterResidentSettingsSwitchText')}
                        viewingMode={props.viewingMode}
                        viewingValue={
                          organisationUnitDetailsHook.selectedOrganisationUnit?.transmitterResidentSettings &&
                          organisationUnitDetailsHook.selectedOrganisationUnit.transmitterResidentSettings.length > 0
                            ? organisationUnitDetailsHook.selectedOrganisationUnit.transmitterResidentSettings
                                ?.map((setting) => deviceTypesTranslations(`${setting}`))
                                .sort((a, b) => a.localeCompare(b))
                                .join(', ')
                            : '-'
                        }
                        formField={
                          <MultiSelectField
                            label={t('transmitterResidentSettingsSwitchText')}
                            options={transmitterResidentSettingOptions
                              .map((deviceType) => ({
                                key: deviceType,
                                value: deviceTypesTranslations(`${deviceType}`),
                              }))
                              .sort((a, b) => a.value.localeCompare(b.value))}
                            field={field}
                            disabled={props.viewingMode === 'viewing'}
                          />
                        }
                      />
                    )}
                  />
                )}
              </div>
            )}

            {getValues(OrganisationFormName.ResidentialLocation) &&
            organisationUnitDetailsHook.selectedOrganisationUnit?.hasEcdConnectorsInParent ? (
              <Controller
                name={OrganisationFormName.ElectronicClientRecordRoom}
                render={() => (
                  <ElectronicClientRecordProvider
                    form={organisationUnitDetailsHook.form}
                    selectedOrganisationUnit={organisationUnitDetailsHook.selectedOrganisationUnit}
                    isLoading={organisationUnitDetailsHook.isLoadingSelectedOrganisationUnit}
                  >
                    <ElectronicClientRecord viewingMode={props.viewingMode} />
                  </ElectronicClientRecordProvider>
                )}
              />
            ) : (
              <></>
            )}

            {props.selectedNode?.isDiversionChannel && (
              <>
                <Divider className="organisation-units-details-divider" startText={t('alternativeDiversionDividerText')} />
                <div className="diversion-channel-description-container">
                  <div className={'noteContainer'}>
                    <Typography variant="h5">{t('alternativeDiversionInfo.title')}</Typography>
                    <Typography className="diversion-channel-description" variant="subtitle1">
                      {t('diversionChannelDescription')}
                    </Typography>
                  </div>
                </div>
                <div className="organisation-settings-container">
                  <SettingsRow
                    text={t('diversionChannelToggleDescription')}
                    formField={<Switch type="checkbox" value={true} checked={true} disabled />}
                  />
                </div>
                <div className="organisation-settings-container">
                  <Controller
                    name={OrganisationFormName.DiversionTimeoutEnabled}
                    control={control}
                    render={({ field }) => {
                      const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                        field.onChange(event);
                        if (event.target.checked) {
                          setValue(OrganisationFormName.DiversionTimeout, 900, { shouldDirty: true });
                        }
                      };
                      return (
                        <SettingsRow
                          text={t('diversionChannelOpenedAlarmToggleDescription')}
                          formField={
                            <Switch
                              {...field}
                              type="checkbox"
                              value={field.value}
                              checked={field.value}
                              // prettier-ignore
                              // @ts-ignore
                              inputProps={{ "data-testid": "isDiversionTimeoutEnabled" }}
                              disabled={props.viewingMode === 'viewing'}
                              onChange={handleChange}
                            />
                          }
                        />
                      );
                    }}
                  />
                </div>
                {getValues(OrganisationFormName.DiversionTimeoutEnabled) && (
                  <div className="organisation-settings-container">
                    <Controller
                      name={OrganisationFormName.DiversionTimeout}
                      control={control}
                      render={({ field }) => (
                        <FormControl className="diversion-timeout-top-margin">
                          <InputLabel>{t('diversionChannelOpenedAlarmToggleDescription')}</InputLabel>
                          <Select
                            value={field.value ?? ''}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                            inputProps={{
                              'data-testid': 'diversionTimeoutInput',
                            }}
                            id="diversionTimeoutInput"
                            variant="outlined"
                            label={t('diversionChannelOpenedAlarmToggleDescription')}
                            disabled={props.viewingMode === 'viewing'}
                          >
                            {diversionTimeoutOptions.map((menuItem) => menuItem)}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </div>
                )}
              </>
            )}

            {getValues('isSubscriptionChannel') &&
              AlternativeDiversionValidator.hasLocationInParents(props.parentNode?.id, props.upstreamParentBranch) && (
                <>
                  <Divider className="organisation-units-details-divider" startText={t('alternativeDiversionDividerText')} />
                  {organisationUnitDetailsHook.isLoadingSelectedOrganisationUnit && <LoadingIndicator />}
                  {!organisationUnitDetailsHook.isLoadingSelectedOrganisationUnit && (
                    <div className="alternative-organisation-units-container">
                      <Controller
                        name={OrganisationFormName.AlternativeDiversionEnabled}
                        control={control}
                        rules={registerOptions.alternativeDiversionEnabled}
                        render={({ field }) => (
                          <>
                            <SettingsRow
                              icon={<NotificationImportantOutlined />}
                              text={t('alternativeDiversionSwitchText')}
                              hasError={!!errors.alternativeDiversionEnabled}
                              helperText={errors.alternativeDiversionEnabled?.message?.toString()}
                              formField={
                                <Switch
                                  {...field}
                                  type="checkbox"
                                  value={field.value}
                                  checked={field.value}
                                  // prettier-ignore
                                  // @ts-ignore
                                  inputProps={{ "data-testid": "alternativeDiversionSwitch" }}
                                  disabled={props.viewingMode === 'viewing' || getValues(OrganisationFormName.IsLocation) === true}
                                />
                              }
                            />
                            <OrganisationDetailsAlternativeDiversionModal
                              isOpen={alternativeDiversionModalIsOpen}
                              onClose={() => setAlternativeDiversionModalIsOpen(false)}
                              onSaveButtonClicked={handleOnAlternativeDiversionLinkButtonClicked}
                              selectedAlternativeDiversions={getValues('alternativeDiversionOrganisationUnits')}
                              organisationUnitId={props.selectedNode?.id ?? props.parentNode!.id}
                            />
                          </>
                        )}
                      />
                      {getValues(OrganisationFormName.AlternativeDiversionEnabled) &&
                        errors.alternativeDiversionEnabled?.message !== t('singleAlternativeDiversionHelperText') && (
                          <div className={'noteContainer'}>
                            <Typography variant="h5">{t('alternativeDiversionInfo.title')}</Typography>
                            <Typography variant="subtitle1">{t('alternativeDiversionInfo.description')}</Typography>
                          </div>
                        )}
                      {getValues(OrganisationFormName.AlternativeDiversionEnabled) &&
                        getValues(OrganisationFormName.AlternativeDiversionOrganisationUnits).map((organisationUnit, i) => (
                          <BreadCrumb
                            key={`breadcrumb-${organisationUnitDetailsHook.selectedOrganisationUnit?.id}-${i}`}
                            showEllipsis={getBreadCrumbs(organisationUnit).length > 2}
                            allElementNames={getBreadCrumbs(organisationUnit)}
                          >
                            {getBreadCrumbs(organisationUnit).map((name, index) => {
                              return (
                                <Typography key={`Breadcrumb_Child_${name}-${index}`} variant="caption">
                                  {name}
                                </Typography>
                              );
                            })}
                          </BreadCrumb>
                        ))}
                      {(props.viewingMode === 'creation' || props.viewingMode === 'editing') &&
                      getValues(OrganisationFormName.AlternativeDiversionEnabled) &&
                      (!errors.alternativeDiversionEnabled || errors.alternativeDiversionEnabled?.type === 'onSubmitOnly') ? (
                        <Button
                          startIcon={<LinkOutlined />}
                          variant="outlined"
                          onClick={handleOnAlternativeDiversionButtonClicked}
                          data-testid="linkAlternativeDiversionButton"
                        >
                          {t('linkAlternativeDiversionButtonText')}
                        </Button>
                      ) : (
                        <></>
                      )}
                    </div>
                  )}
                </>
              )}

            {getValues(OrganisationFormName.IsSubscriptionChannel) &&
              organisationUnitDetailsHook.selectedOrganisationUnit?.emergencyAlarmEnabledSetting &&
              EmergencyAlarmRouteValidator.hasLocationInParents(props.parentNode?.id, props.upstreamParentBranch) && (
                <>
                  <Divider className="organisation-units-details-divider" startText={t('emergencyAlarmRouteDividerText')} />
                  {organisationUnitDetailsHook.isLoadingSelectedOrganisationUnit && <LoadingIndicator />}
                  {!organisationUnitDetailsHook.isLoadingSelectedOrganisationUnit && (
                    <div className="emergency-alarm-route-organisation-units-container">
                      <Controller
                        name={OrganisationFormName.EmergencyAlarmRoutesEnabled}
                        control={control}
                        rules={registerOptions.emergencyAlarmRouteEnabled}
                        render={({ field }) => (
                          <>
                            <SettingsRow
                              icon={<SecurityRounded />}
                              text={t('emergencyAlarmRouteSwitchText')}
                              hasError={!!errors.emergencyAlarmRoutesEnabled}
                              helperText={errors.emergencyAlarmRoutesEnabled?.message?.toString()}
                              formField={
                                <Switch
                                  {...field}
                                  type="checkbox"
                                  value={field.value}
                                  checked={field.value}
                                  // prettier-ignore
                                  // @ts-ignore
                                  inputProps={{ "data-testid": "emergencyAlarmRouteSwitch" }}
                                  disabled={props.viewingMode === 'viewing' || getValues(OrganisationFormName.IsLocation) === true}
                                />
                              }
                            />
                            <OrganisationDetailsEmergencyAlarmRouteModal
                              isOpen={emergencyAlarmRoutesModalIsOpen}
                              onClose={() => setEmergencyAlarmRoutesModalIsOpen(false)}
                              onSaveButtonClicked={handleOnEmergencyAlarmRouteLinkButtonClicked}
                              selectedEmergencyAlarmRouteUnits={getValues(OrganisationFormName.EmergencyAlarmRouteOrganisationUnits)}
                              organisationUnitId={props.selectedNode?.id ?? props.parentNode!.id}
                            />
                          </>
                        )}
                      />
                      {getValues(OrganisationFormName.EmergencyAlarmRoutesEnabled) &&
                        errors.emergencyAlarmRoutesEnabled?.message !== t('singleAlternativeEmergencyAlarmRuleHelperText') && (
                          <div className={'noteContainer'}>
                            <Typography variant="h5">{t('emergencyAlarmRouteInfo.title')}</Typography>
                            <Typography variant="subtitle1">{t('emergencyAlarmRouteInfo.description')}</Typography>
                          </div>
                        )}
                      {getValues(OrganisationFormName.EmergencyAlarmRoutesEnabled) &&
                        getValues(OrganisationFormName.EmergencyAlarmRouteOrganisationUnits).map((organisationUnit, i) => (
                          <BreadCrumb
                            key={`breadcrumb-${organisationUnitDetailsHook.selectedOrganisationUnit?.id}-${i}`}
                            showEllipsis={getBreadCrumbs(organisationUnit).length > 2}
                            allElementNames={getBreadCrumbs(organisationUnit)}
                          >
                            {getBreadCrumbs(organisationUnit).map((name, index) => {
                              return (
                                <Typography key={`Breadcrumb_Child_${name}-${index}`} variant="caption">
                                  {name}
                                </Typography>
                              );
                            })}
                          </BreadCrumb>
                        ))}
                      {(props.viewingMode === 'creation' || props.viewingMode === 'editing') &&
                      getValues(OrganisationFormName.EmergencyAlarmRoutesEnabled) &&
                      (!errors.emergencyAlarmRoutesEnabled || errors.emergencyAlarmRoutesEnabled?.type === 'onSubmitOnly') ? (
                        <Button
                          startIcon={<LinkOutlined />}
                          variant="outlined"
                          onClick={handleOnEmergencyAlarmRoutesButtonClicked}
                          data-testid="linkEmergencyAlarmRouteButton"
                        >
                          {t('linkEmergencyAlarmRouteButtonText')}
                        </Button>
                      ) : (
                        <></>
                      )}
                    </div>
                  )}
                </>
              )}

            {hasPermission(Permission.ReadDevice) && (
              <>
                <Divider
                  className="organisation-units-details-divider"
                  startText={t('devicesDividerText')}
                  endElement={
                    props.viewingMode === 'creation' || props.viewingMode === 'editing' ? (
                      hasPermission(Permission.LinkDevice) ? (
                        <Button
                          startIcon={<LinkOutlined />}
                          variant="outlined"
                          onClick={handleOnDeviceConnectionButtonClicked}
                          data-testid="linkDeviceButton"
                        >
                          {t('linkDeviceButtonText')}
                        </Button>
                      ) : undefined
                    ) : (
                      <></>
                    )
                  }
                />

                {organisationUnitDetailsHook.isLoadingSelectedOrganisationUnit && <LoadingIndicator />}
                {!organisationUnitDetailsHook.isLoadingSelectedOrganisationUnit && (props.selectedNode || props.parentNode) && (
                  <Controller
                    name="devices"
                    control={control}
                    render={({ field }) => (
                      <>
                        <DevicesProvider>
                          <OrganisationDetailsDevicesTable
                            noDevicesText={t('devicesTable.noDevicesLinked')}
                            linkedDevices={field.value}
                            unlinkedDevices={unlinkedDevices}
                            viewingMode={props.viewingMode}
                            onUnlinkButtonClicked={handleOnUnlinkDeviceButtonClicked}
                            onRelinkButtonClicked={(selectedDevice: OrganisationUnitDevice) =>
                              handleOnDevicesLinkButtonClicked([selectedDevice])
                            }
                          />
                        </DevicesProvider>
                        <DeviceLinkProvider
                          organisationUnitId={props.selectedNode?.id ?? props.parentNode!.id}
                          devicesToHide={field.value as LinkableDevice[]}
                        >
                          <OrganisationDetailsLinkableDevicesModal
                            isOpen={linkableDevicesModalIsOpen}
                            onClose={() => setLinkableDevicesModalIsOpen(false)}
                            onLinkButtonClicked={handleOnDevicesLinkButtonClicked}
                          />
                        </DeviceLinkProvider>
                      </>
                    )}
                  />
                )}
              </>
            )}

            {!getValues('isLocation') && (
              <Divider
                className="organisation-units-details-divider"
                startText={t('scenariosDividerText')}
                endElement={
                  props.viewingMode === 'creation' || props.viewingMode === 'editing' ? (
                    hasPermission(Permission.LinkScenario) ? (
                      <Button
                        startIcon={<LinkOutlined />}
                        variant="outlined"
                        onClick={handleOnScenarioConnectionButtonClicked}
                        data-testid="linkScenarioButton"
                      >
                        {t('linkScenarioButtonText')}
                      </Button>
                    ) : undefined
                  ) : (
                    <></>
                  )
                }
              />
            )}

            {organisationUnitDetailsHook.isLoadingSelectedOrganisationUnit && <LoadingIndicator />}
            {!organisationUnitDetailsHook.isLoadingSelectedOrganisationUnit &&
              !getValues(OrganisationFormName.IsLocation) &&
              (props.selectedNode || props.parentNode) && (
                <Controller
                  name="scenarios"
                  control={control}
                  render={({ field }) => (
                    <>
                      <OrganisationDetailsScenariosList
                        linkedScenarios={field.value}
                        unlinkedScenarios={unlinkedScenarios}
                        viewingMode={props.viewingMode}
                        onUnlinkButtonClicked={handleOnUnlinkScenarioButtonClicked}
                        onRelinkButtonClicked={(selectedScenario: Scenario) => handleOnScenariosLinkButtonClicked([selectedScenario])}
                      />
                      <OrganisationDetailsLinkableScenariosModal
                        isOpen={linkableScenariosModalIsOpen}
                        onClose={() => setLinkableScenariosModalIsOpen(false)}
                        onLinkButtonClicked={handleOnScenariosLinkButtonClicked}
                        scenariosToHide={field.value}
                      />
                    </>
                  )}
                />
              )}

            <PopUp
              isOpen={shouldShowDeleteConfirmationPopup}
              title={t('deleteConfirmationPopup.title', {
                name: props.selectedNode?.name,
              })}
              body={t('deleteConfirmationPopup.body')}
              primaryButtonText={t('deleteConfirmationPopup.primaryButtonText')}
              secondaryButtonText={t('deleteConfirmationPopup.secondaryButtonText')}
              handleOnClose={() => setShouldShowDeleteConfirmationPopup(false)}
              secondaryButtonAction={() => setShouldShowDeleteConfirmationPopup(false)}
              primaryButtonAction={handleDeleteOrganisationUnit}
            />

            <PopUp
              isOpen={shouldShowErrorDeletePopup}
              title={t('deleteErrorPopup.title', {
                name: props.selectedNode?.name,
              })}
              body={errorDeletePopupContent}
              primaryButtonText={t('deleteErrorPopup.primaryButtonText')}
              handleOnClose={() => setShouldShowErrorDeletePopup(false)}
              primaryButtonAction={() => setShouldShowErrorDeletePopup(false)}
            />

            <PopUp
              isOpen={shouldShowErrorEditSavePopup}
              title={t(props.viewingMode === 'creation' ? 'saveEditErrorPopup.saveTitle' : 'saveEditErrorPopup.editTitle', {
                name: getValues(OrganisationFormName.Name).trim(),
              })}
              body={errorEditSavePopupContent}
              primaryButtonText={t('deleteErrorPopup.primaryButtonText')}
              handleOnClose={() => setShouldShowErrorEditSavePopup(false)}
              primaryButtonAction={() => setShouldShowErrorEditSavePopup(false)}
            />

            <PopUp
              isOpen={shouldShowIsLocationConfirmationPopup}
              title={t('isLocationConfirmationPopup.title')}
              body={t('isLocationConfirmationPopup.body')}
              primaryButtonText={t('isLocationConfirmationPopup.primaryButtonText')}
              secondaryButtonText={t('isLocationConfirmationPopup.secondaryButtonText')}
              handleOnClose={() => {
                setShouldShowIsLocationConfirmationPopup(false);
                setIsSubmitting(false);
              }}
              secondaryButtonAction={() => {
                setShouldShowIsLocationConfirmationPopup(false);
                setIsSubmitting(false);
              }}
              primaryButtonAction={() => {
                handleSubmitForm();
                setShouldShowIsLocationConfirmationPopup(false);
              }}
            />
          </div>
        </form>
      </FormProvider>

      <PopUp
        isOpen={unlinkConfirmationWarningCurrentDevice !== undefined}
        title={t('unlinkConfirmationWarning.title')}
        body={t('unlinkConfirmationWarning.bodyText')}
        primaryButtonText={t('unlinkConfirmationWarning.confirmButton')}
        secondaryButtonText={t('unlinkConfirmationWarning.backButton')}
        handleOnClose={() => setUnlinkConfirmationWarningCurrentDevice(undefined)}
        primaryButtonAction={() => handleWarningConfirmed()}
        secondaryButtonAction={() => setUnlinkConfirmationWarningCurrentDevice(undefined)}
      />

      <PopUp
        isOpen={showIpUnitUnreachableError}
        title={t('ipUnitUnreachable.title')}
        body={t('ipUnitUnreachable.bodyText')}
        primaryButtonText={t('ipUnitUnreachable.confirmButton')}
        handleOnClose={() => setShowIpUnitUnreachableError(false)}
        primaryButtonAction={() => setShowIpUnitUnreachableError(false)}
      />
    </div>
  );
};

export default OrganisationDetails;
